import type { ProductRequestType } from "@v1/hooks/useStoreProducts";
import type { ProductRef, ProductRefOrDynamic } from "schemas/product";

import * as React from "react";

import { arrayMove } from "@dnd-kit/sortable";
import twMerge from "@replo/design-system/utils/twMerge";
import DynamicDataButton from "@v1/components/common/designSystem/DynamicDataButton";
import { DynamicDataSelector } from "@v1/components/editor/page/DynamicDataSelector";
import DynamicDataValueIndicator from "@v1/components/editor/page/element-editor/components/extras/DynamicDataValueIndicator";
import {
  ProductSelectionButton,
  ProductSelectionCombobox,
} from "@v1/components/editor/page/ProductSelectionCombobox";
import {
  SortableItem,
  SortableList,
} from "@v2/features/products/components/SortableList";
import { isContextRef } from "replo-runtime/store/ReploProduct";

type MultiProductProps = {
  isMultiProducts: true;
  onChange: (value: ProductRef[]) => void;
  selectedProductRef: ProductRef[] | null;
};

type SingleProductProps = {
  isMultiProducts?: false;
  onChange: (value: ProductRef | null) => void;
  selectedProductRef: ProductRefOrDynamic | null;
};

type ProductSelectorSharedProps = {
  excludedDynamicDataKeys?: string[];
  allowDynamicData?: boolean;
  productRequestType?: ProductRequestType;
  isVariantSelectable?: boolean;
  className?: string;
  showDeleteButton?: boolean;
  noDynamicData?: boolean;
  side?: "top" | "bottom" | "left" | "right";
  sideOffset?: number;
  size?: "sm" | "base";
  onRemove?: () => void;
  triggerLayoutClassName?: string;
  placeholderText?: string;
};

type SingleProductSelectorProps = SingleProductProps &
  ProductSelectorSharedProps;

type MultiProductSelectorProps = MultiProductProps & ProductSelectorSharedProps;

export function ProductSelector(
  props: SingleProductSelectorProps,
): React.ReactElement<any, any> | null;
export function ProductSelector(
  props: MultiProductSelectorProps,
): React.ReactElement<any, any> | null;

export function ProductSelector({
  selectedProductRef,
  allowDynamicData = true,
  onChange,
  isMultiProducts,
  isVariantSelectable = false,
  className,
  side = "bottom",
  sideOffset = 4,
  size = "base",
  onRemove,
  triggerLayoutClassName,
  placeholderText,
}: ProductSelectorSharedProps & (MultiProductProps | SingleProductProps)) {
  if (
    !isMultiProducts &&
    selectedProductRef &&
    isContextRef(selectedProductRef)
  ) {
    return (
      <DynamicDataSelector
        targetType={isVariantSelectable ? "productVariant" : "productOnly"}
        initialPath={selectedProductRef.ref.split(".")}
        side="left"
        sideOffset={12}
        trigger={
          <DynamicDataValueIndicator
            type="other"
            templateValue={`{{${selectedProductRef.ref}}}`}
            onRemove={() => {
              onChange(null);
            }}
          />
        }
        onChange={(value) => {
          (onChange as (value: ProductRefOrDynamic) => void)(value);
        }}
      />
    );
  }

  return (
    <div
      className={twMerge(
        "flex flex-row items-center justify-between gap-2",
        className,
      )}
    >
      {isMultiProducts ? (
        <div className="flex-1">
          <SortableList
            onReorderEnd={({ oldIndex, newIndex }) => {
              const reorderedList = arrayMove(
                (selectedProductRef ?? []) as ProductRef[],
                oldIndex,
                newIndex,
              );
              onChange(reorderedList);
            }}
            withDragHandle
          >
            {((selectedProductRef ?? []) as ProductRef[]).map(
              (productRef, index) => (
                <SortableItem
                  key={productRef?.id ?? `undefined-${index}`}
                  id={productRef?.id ?? `undefined-${index}`}
                >
                  <ProductSelectionCombobox
                    value={selectedProductRef ?? []}
                    onChange={onChange}
                    isMultiselect
                    side={side}
                    sideOffset={sideOffset}
                    trigger={
                      <ProductSelectionButton
                        size={size}
                        title={productRef.title ?? "Edit Products"}
                        // Prevent drag from triggering click
                        onMouseDown={(e) => e.stopPropagation()}
                        onRemove={() => {
                          const updatedProducts = [
                            ...(selectedProductRef ?? []),
                          ];
                          updatedProducts.splice(index, 1);
                          onChange(updatedProducts);
                        }}
                        layoutClassName={triggerLayoutClassName}
                      />
                    }
                  />
                </SortableItem>
              ),
            )}
          </SortableList>
          <ProductSelectionCombobox
            value={selectedProductRef ?? []}
            onChange={onChange}
            isMultiselect
            side={side}
            sideOffset={sideOffset}
            trigger={
              <ProductSelectionButton
                size={size}
                title="Edit Products"
                allowDynamicData
                isPlaceholder
                layoutClassName={twMerge(
                  "mt-2 border-dashed",
                  triggerLayoutClassName,
                )}
              />
            }
          />
        </div>
      ) : (
        <ProductSelectionCombobox
          size={size}
          // Note (Noah, 2025-04-02): Typescript doesn't understand that this must not
          // be dynamic, even though we did a check for isContextRef above
          value={selectedProductRef as ProductRef | null}
          onChange={onChange}
          side={side}
          sideOffset={sideOffset}
          onRemove={onRemove}
          triggerLayoutClassName={triggerLayoutClassName}
          placeholderText={placeholderText}
          allowDynamicData={allowDynamicData}
        />
      )}
      {allowDynamicData && (
        <DynamicDataSelector
          side="left"
          sideOffset={222}
          targetType={isVariantSelectable ? "productVariant" : "productOnly"}
          trigger={<DynamicDataButton />}
          onChange={(value: any) => {
            onChange(value);
          }}
        />
      )}
    </div>
  );
}
